import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  coatTypeIndexQueryKey,
  coatTypeShowOptions,
  coatTypeShowQueryKey,
  coatTypeUpdateMutation,
} from '$api-client/@tanstack/react-query.gen'
import { UpdateCoatTypeForm, UpdateCoatTypeSchema } from '$features/settings/forms/coat-type/update'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { Router } from '$utils/router'
import { NavigableModal, Text, LoaderIcon } from '$components'
import { ToastHelper, ToastType } from '$components'
import { useNavigate, useParams } from 'react-router'

export function UpdateCoatTypeModal() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { coatTypeId } = useParams()

  const handleClose = () => navigate(Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsPetOptionsCoats))

  const { data: coatType, isLoading } = useQuery({
    ...coatTypeShowOptions({
      path: { coatType: Number(coatTypeId) },
    }),
  })

  const mutation = useMutation({
    ...coatTypeUpdateMutation(),
    onSuccess: async () => {
      ToastHelper.updateSuccess(ToastType.CoatType)
      queryClient.invalidateQueries({ queryKey: coatTypeIndexQueryKey() })
      await queryClient.invalidateQueries({
        queryKey: coatTypeShowQueryKey({ path: { coatType: Number(coatTypeId) } }),
      })
      handleClose()
    },
    onError: () => ToastHelper.updateError(ToastType.CoatType),
  })

  const handleSubmit = (values: UpdateCoatTypeSchema) => {
    mutation.mutate({
      path: { coatType: Number(coatTypeId) },
      body: { name: values.name },
    })
  }

  if (isLoading) {
    return (
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>Edit Coat Type</NavigableModal.Heading>
        </NavigableModal.Header>
        <NavigableModal.Content>
          <LoaderIcon className="size-5 my-stack-hero mx-auto" />
        </NavigableModal.Content>
      </NavigableModal>
    )
  }

  if (!coatType) {
    return (
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>Edit Coat Type</NavigableModal.Heading>
        </NavigableModal.Header>
        <NavigableModal.Content className="flex items-center justify-center my-stack-hero">
          <Text type="s">Couldn&apos;t find coat type.</Text>
        </NavigableModal.Content>
      </NavigableModal>
    )
  }

  return (
    <UpdateCoatTypeForm
      isLoading={mutation.isPending}
      onSubmit={handleSubmit}
      onCancel={handleClose}
      defaultValues={{
        name: coatType.data.values?.name,
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>Edit Coat Type</NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <UpdateCoatTypeForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <UpdateCoatTypeForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </UpdateCoatTypeForm>
  )
}
